import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Box,
  Typography,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  addPromptType,
  updatePromptType,
  deletePromptType
} from '../../../store/promptTemplateSlice';
import { COLORS } from '../../../shared/colors';
import { MOCK_PROMPT_TYPES } from '../../../shared/mockData';

const PromptTypeModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { types = MOCK_PROMPT_TYPES, templates = [] } = useSelector(state => state.promptTemplates);
  
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const getTemplateCount = (typeName) => {
    return templates.filter(t => t.type === typeName).length;
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({
      name: type.name,
      description: type.description
    });
  };

  const handleSave = () => {
    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (editingType) {
      dispatch(updatePromptType({ ...editingType, ...formData }));
      toast.success('Type updated successfully!');
    } else {
      dispatch(addPromptType(formData));
      toast.success('Type created successfully!');
    }
    
    setEditingType(null);
    setFormData({
      name: '',
      description: ''
    });
  };

  const handleDelete = (type) => {
    const templateCount = getTemplateCount(type.name);
    if (templateCount > 0) {
      toast.error(`Cannot delete type with ${templateCount} existing templates`);
      return;
    }
    
    dispatch(deletePromptType(type.id));
    toast.success('Type deleted successfully!');
  };

  const handleCancel = () => {
    setEditingType(null);
    setFormData({
      name: '',
      description: ''
    });
  };

  const handleChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => {
        setEditingType(null);
        setFormData({ name: '', description: '' });
        onClose();
      }} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.blue[600]} 100%)`,
          color: 'white',
          fontWeight: 700,
          fontSize: '1.25rem',
          py: 2.5,
          px: 3
        }}
      >
        Manage Prompt Types
        <IconButton 
          onClick={() => {
            setEditingType(null);
            setFormData({ name: '', description: '' });
            onClose();
          }} 
          size="small"
          sx={{ 
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {editingType !== null && (
          <Box sx={{ 
            m: 2.5, 
            p: 2.5, 
            bgcolor: COLORS.blue[50], 
            borderRadius: 2,
            border: `1px solid ${COLORS.blue[200]}`,
            boxShadow: '0 2px 8px rgba(59, 130, 246, 0.1)'
          }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              {editingType && editingType.id ? 'Edit Type' : 'Create New Type'}
            </Typography>
            <TextField
              label="Type Name"
              placeholder="Enter prompt type name"
              value={formData.name}
              onChange={handleChange('name')}
              fullWidth
              size="small"
              sx={{ 
                mb: 2,
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  transform: 'translate(14px, 9px) scale(1)'
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)'
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem'
                }
              }}
            />
            <TextField
              label="Description"
              placeholder="Describe what this prompt type is used for"
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={2}
              fullWidth
              size="small"
              sx={{ 
                mb: 2,
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  transform: 'translate(14px, 9px) scale(1)'
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -9px) scale(0.75)'
                },
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem'
                }
              }}
            />
            <Box display="flex" gap={1}>
              <Button onClick={handleSave} variant="contained" size="small">
                {editingType && editingType.id ? 'Update' : 'Create'}
              </Button>
              <Button onClick={handleCancel} variant="outlined" size="small">
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ px: 3, pt: editingType === null ? 3 : 0 }}>
          <TableContainer 
            component={Paper} 
            sx={{ 
              maxHeight: 450,
              borderRadius: 0,
              boxShadow: 'none',
              border: 'none'
            }}
          >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    bgcolor: COLORS.blue[100],
                    fontWeight: 700,
                    color: COLORS.gray[800],
                    borderBottom: 'none',
                    px: 3
                  }}
                >
                  Type Name
                </TableCell>
                <TableCell 
                  sx={{ 
                    bgcolor: COLORS.blue[100],
                    fontWeight: 700,
                    color: COLORS.gray[800],
                    borderBottom: 'none',
                    px: 3
                  }}
                >
                  Description
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    bgcolor: COLORS.blue[100],
                    fontWeight: 700,
                    color: COLORS.gray[800],
                    borderBottom: 'none',
                    px: 3
                  }}
                >
                  Templates
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    bgcolor: COLORS.blue[100],
                    fontWeight: 700,
                    color: COLORS.gray[800],
                    borderBottom: 'none',
                    px: 3
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {types.map((type) => (
                <TableRow key={type.id} hover>
                  <TableCell sx={{ px: 3 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {type.name}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ px: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {type.description}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ px: 3 }}>
                    <Chip 
                      label={getTemplateCount(type.name)} 
                      size="small"
                      sx={{ bgcolor: COLORS.gray[100] }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ px: 3 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(type)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDelete(type)}
                        disabled={getTemplateCount(type.name) > 0}
                        sx={{
                          color: COLORS.error,
                          '&:hover': { bgcolor: COLORS.red[50] }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, bgcolor: COLORS.gray[25] }}>
        <Button 
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingType({});
            setFormData({ name: '', description: '' });
          }}
          variant="contained"
          sx={{ 
            bgcolor: COLORS.primary,
            px: 3,
            py: 1,
            fontWeight: 600,
            borderRadius: 2,
            '&:hover': { bgcolor: COLORS.blue[600] }
          }}
        >
          Add New Type
        </Button>
        <Button 
          onClick={() => {
            setEditingType(null);
            setFormData({ name: '', description: '' });
            onClose();
          }}
          variant="outlined"
          sx={{ 
            color: COLORS.primary,
            borderColor: COLORS.primary,
            px: 3,
            py: 1,
            fontWeight: 600,
            borderRadius: 2,
            '&:hover': {
              bgcolor: COLORS.blue[50],
              borderColor: COLORS.blue[600]
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptTypeModal;